import { ViewIds } from '@ardoq/api-types';
import {
  isViewWithoutFullyMultiLabelsSupport,
  isViewWithoutFullyGroupingSupport,
} from 'views/environmentals/getViewpointModeSupportedViews';
import {
  getHasMultipleLabels,
  hasComponentFieldLabels,
  hasReferenceLabelFormattingRules,
} from 'perspectiveSidebar/perspectiveEditor/multiLabelUtils';
import { LabelFormattingInfo } from '@ardoq/data-model';
import { PerspectiveGroupingRule } from '@ardoq/perspectives';
import { t, type I18nLocale } from '@ardoq/i18n';

export type ViewSelectorWarningCode =
  | 'relationships.firstLabelOnly'
  | 'timeline.referenceLabelsHidden'
  | 'bubble.firstLabelOnlyAndReferenceLabelsAndGroupsHidden'
  | 'bubble.firstLabelOnlyAndGroupsHidden'
  | 'bubble.groupsAndReferenceLabelsHidden'
  | 'bubble.firstLabelOnlyAndReferenceLabelsHidden'
  | 'bubble.firstLabelOnly'
  | 'bubble.referenceLabelsHidden'
  | 'bubble.groupsHidden'
  | 'hierarchical.fieldLabelsHidden'
  | 'hierarchical.referenceLabelsHidden'
  | 'hierarchical.fieldLabelsAndReferenceLabelsHidden';

type WarningMessageParams = {
  viewId: ViewIds;
  labelFormatting: LabelFormattingInfo[];
  groupingRules: PerspectiveGroupingRule[];
};

type FormattingState = {
  hasComponentMultiLabels: boolean;
  /**
   * Any component label rule carrying a field, which is a different question from having more
   * than one.
   *
   * The views that say "only the first field will show" are only wrong once there are two, so
   * `getHasMultipleLabels` is the right test for them. The hierarchical views show none, so the
   * first field is already a field being dropped.
   */
  hasComponentLabels: boolean;
  hasReferenceLabels: boolean;
  hasReferenceMultiLabels: boolean;
  hasGroups: boolean;
};

const getFormattingState = (
  labelFormatting: LabelFormattingInfo[],
  groupingRules: PerspectiveGroupingRule[]
): FormattingState => ({
  hasComponentMultiLabels: getHasMultipleLabels(labelFormatting, 'component'),
  hasComponentLabels: hasComponentFieldLabels(labelFormatting),
  hasReferenceLabels: hasReferenceLabelFormattingRules(labelFormatting),
  hasReferenceMultiLabels: getHasMultipleLabels(labelFormatting, 'reference'),
  hasGroups: Boolean(groupingRules && groupingRules.length > 0),
});

const getRelationshipsViewWarningCode = (
  state: FormattingState
): ViewSelectorWarningCode | undefined => {
  const { hasComponentMultiLabels, hasReferenceMultiLabels } = state;
  return hasComponentMultiLabels || hasReferenceMultiLabels
    ? 'relationships.firstLabelOnly'
    : undefined;
};

/**
 * The 3D network view, which has the label limitations but not the grouping one.
 *
 * Its own handler rather than `getBubbleViewWarningCode`, because that one branches on `hasGroups`
 * whatever view asked - and this is the one view in the map that *draws* groups. Sharing it told a
 * reader "Groups are not shown in this view style" about the view where a grouping rule overrides
 * the layout setting in order to show them.
 *
 * `getViewSelectorWarningCode` only returns early for a view with full multi-label and grouping
 * support, and this one lacks full multi-label support, so the handler runs and has to get
 * `hasGroups` right itself. `getRestrictedGroupingWarningMessage` avoids the trap by gating on
 * `isViewWithoutFullyGroupingSupport` instead of on this map.
 */
const getNetwork3DViewWarningCode = (
  state: FormattingState
): ViewSelectorWarningCode | undefined => {
  const { hasComponentMultiLabels, hasReferenceLabels } = state;

  if (hasComponentMultiLabels && hasReferenceLabels) {
    return 'bubble.firstLabelOnlyAndReferenceLabelsHidden';
  }
  if (hasComponentMultiLabels) return 'bubble.firstLabelOnly';
  if (hasReferenceLabels) return 'bubble.referenceLabelsHidden';
  return undefined;
};

const getTimelineViewWarningCode = (
  state: FormattingState
): ViewSelectorWarningCode | undefined => {
  const { hasReferenceLabels } = state;
  return hasReferenceLabels ? 'timeline.referenceLabelsHidden' : undefined;
};

/**
 * The three hierarchical views, which show a component's name and nothing else.
 *
 * Stronger than the "first label only" the relationships and bubble views warn about: these take
 * `getGraphNodeOrGroupLabelForViewpointMode(...).mainLabel` and discard the other labels
 * entirely, because a tile, a circle and a ring have no room beneath a name. They draw no
 * references either. Grouping is not mentioned - unlike the bubble chart, these three group.
 */
const getHierarchicalViewWarningCode = (
  state: FormattingState
): ViewSelectorWarningCode | undefined => {
  /* `hasComponentLabels`, not `hasComponentMultiLabels`. The latter counts, and returns false for
     one rule carrying one field - which is the ordinary configuration and the exact case this
     warning exists for. These views keep only the component's name, so a single dropped label is
     the whole complaint. */
  const { hasComponentLabels, hasReferenceLabels } = state;

  if (hasComponentLabels && hasReferenceLabels) {
    return 'hierarchical.fieldLabelsAndReferenceLabelsHidden';
  }
  if (hasComponentLabels) return 'hierarchical.fieldLabelsHidden';
  if (hasReferenceLabels) return 'hierarchical.referenceLabelsHidden';

  return undefined;
};

const getBubbleViewWarningCode = (
  state: FormattingState
): ViewSelectorWarningCode | undefined => {
  const { hasComponentMultiLabels, hasReferenceLabels, hasGroups } = state;

  if (!hasComponentMultiLabels && !hasReferenceLabels && !hasGroups) {
    return undefined;
  }

  if (hasComponentMultiLabels && hasReferenceLabels && hasGroups) {
    return 'bubble.firstLabelOnlyAndReferenceLabelsAndGroupsHidden';
  }

  if (hasComponentMultiLabels && hasGroups) {
    return 'bubble.firstLabelOnlyAndGroupsHidden';
  }

  if (hasReferenceLabels && hasGroups) {
    return 'bubble.groupsAndReferenceLabelsHidden';
  }

  if (hasComponentMultiLabels && hasReferenceLabels) {
    return 'bubble.firstLabelOnlyAndReferenceLabelsHidden';
  }

  if (hasComponentMultiLabels) {
    return 'bubble.firstLabelOnly';
  }

  if (hasReferenceLabels) {
    return 'bubble.referenceLabelsHidden';
  }

  if (hasGroups) {
    return 'bubble.groupsHidden';
  }

  return undefined;
};

const warningCodeHandlers: Partial<
  Record<
    ViewIds,
    (state: FormattingState) => ViewSelectorWarningCode | undefined
  >
> = {
  [ViewIds.RELATIONSHIPS_3]: getRelationshipsViewWarningCode,
  [ViewIds.TIMELINE]: getTimelineViewWarningCode,
  [ViewIds.BUBBLE]: getBubbleViewWarningCode,
  [ViewIds.HIERARCHICAL_TREEMAP]: getHierarchicalViewWarningCode,
  [ViewIds.HIERARCHICAL_PACKING]: getHierarchicalViewWarningCode,
  [ViewIds.SUNBURST]: getHierarchicalViewWarningCode,
  /* The same three limitations, so the same three messages. The `bubble.` prefix on the codes
     is where these strings were first written rather than a claim about which view they
     describe - every one of them says "this view style" rather than naming the bubble chart.

     Needed because the two support flags have a third reader. `getRestrictedGroupingWarningMessage`
     and its multi-label sibling are `exhaustiveCheck`-guarded, so a view without full support
     fails the build until it has a message there; this map is a `Partial<Record<…>>`, so it does
     not ask. */
  [ViewIds.SURFACE_3D]: getBubbleViewWarningCode,
  [ViewIds.CONTOUR]: getBubbleViewWarningCode,
  [ViewIds.SPIDER_2]: getBubbleViewWarningCode,
  [ViewIds.POLAR]: getBubbleViewWarningCode,
  [ViewIds.SCATTER_3D]: getBubbleViewWarningCode,
  [ViewIds.BUBBLE_3D]: getBubbleViewWarningCode,
  /* Its own handler, not the shared one: this view lacks full multi-label support but not
     grouping support, so it has the label limitations and none of the grouping limitation. */
  [ViewIds.NETWORK_3D]: getNetwork3DViewWarningCode,
};

export const getViewSelectorWarningCode = ({
  viewId,
  labelFormatting,
  groupingRules,
}: WarningMessageParams): ViewSelectorWarningCode | undefined => {
  if (
    !isViewWithoutFullyMultiLabelsSupport(viewId) &&
    !isViewWithoutFullyGroupingSupport(viewId)
  ) {
    return undefined;
  }

  const formattingState = getFormattingState(labelFormatting, groupingRules);

  const handler = warningCodeHandlers[viewId];
  if (handler) {
    return handler(formattingState);
  }

  return undefined;
};

export const translateViewSelectorWarning = (
  code: ViewSelectorWarningCode | undefined,
  i18nLocale: I18nLocale
): string | undefined => {
  if (code === undefined) return undefined;
  switch (code) {
    case 'relationships.firstLabelOnly':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.relationships.firstLabelOnly',
        'Only the first field for each component and reference type will show as a label in this view style'
      );
    case 'timeline.referenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.timeline.referenceLabelsHidden',
        'Reference labels are not shown in this view style.'
      );
    case 'bubble.firstLabelOnlyAndReferenceLabelsAndGroupsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.firstLabelOnlyAndReferenceLabelsAndGroupsHidden',
        'Only the first field for each component type will show as a label. Reference labels and groups are not shown in this view style.'
      );
    case 'bubble.firstLabelOnlyAndGroupsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.firstLabelOnlyAndGroupsHidden',
        'Only the first field for each component type will show as a label. Groups are not shown in this view style.'
      );
    case 'bubble.groupsAndReferenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.groupsAndReferenceLabelsHidden',
        'Groups and reference labels are not shown in this view style.'
      );
    case 'bubble.firstLabelOnlyAndReferenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.firstLabelOnlyAndReferenceLabelsHidden',
        'Only the first field for each component type will show as a label. Reference labels are not shown in this view style.'
      );
    case 'bubble.firstLabelOnly':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.firstLabelOnly',
        'Only the first field for each component type will show as a label in this view style.'
      );
    case 'bubble.referenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.referenceLabelsHidden',
        'Reference labels are not shown in this view style.'
      );
    case 'bubble.groupsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.bubble.groupsHidden',
        'Groups are not shown in this view style.'
      );
    case 'hierarchical.fieldLabelsAndReferenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.hierarchical.fieldLabelsAndReferenceLabelsHidden',
        'Only the component name is shown. Field labels and reference labels are not shown in this view style.'
      );
    case 'hierarchical.fieldLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.hierarchical.fieldLabelsHidden',
        'Only the component name is shown. Field labels are not shown in this view style.'
      );
    case 'hierarchical.referenceLabelsHidden':
      return t(
        i18nLocale,
        'viewpointBuilder:metaInfo.viewWarning.hierarchical.referenceLabelsHidden',
        'Reference labels are not shown in this view style.'
      );
    default:
      code satisfies never;
      return undefined;
  }
};
